"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;
var _regenerator = _interopRequireDefault(require("@babel/runtime/regenerator"));
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _asyncToGenerator2 = _interopRequireDefault(require("@babel/runtime/helpers/asyncToGenerator"));
var _defer = _interopRequireDefault(require("lodash/defer"));
var _each = _interopRequireDefault(require("lodash/each"));
var _find = _interopRequireDefault(require("lodash/find"));
var _isFunction = _interopRequireDefault(require("lodash/isFunction"));
var _keys = _interopRequireDefault(require("lodash/keys"));
var _events = _interopRequireDefault(require("events"));
var _debugger = _interopRequireDefault(require("./debugger"));
var _streamUnwrap = require("./utils/streamUnwrap");
var _isAppleWebkitBrowser = _interopRequireDefault(require("./utils/isAppleWebkitBrowser"));
var _StreamPlayer = _interopRequireDefault(require("./player/StreamPlayer"));
var _deferredPromise = _interopRequireDefault(require("./utils/deferredPromise"));
var _isDebugEnabled = _interopRequireDefault(require("./utils/isDebugEnabled"));
var _ignoreChromeMediaEvents = _interopRequireDefault(require("./utils/ignoreChromeMediaEvents"));
var _webApiClientSingleton = _interopRequireDefault(require("./webApiClientSingleton"));
var _nowPlayingPoller = _interopRequireDefault(require("./nowPlayingPoller"));
var _listenTime = _interopRequireDefault(require("./loggers/listenTime"));
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2["default"])(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var emitter = new _events["default"]();

// TODO: commenting out for now; will bring back when we start retry logic again for streams.
// const defaultRetryLimit = 0;
var defaultTimeout = 15000;

/**
 *  This is the main entry point into the application to handle all listening and
 *  reporting activities.
 *
 * @module Tuner
 */
var tuner = {
  /**
   * Enum for player events that can be fired
   *
   * @typedef PlayerEvents
   * @property {string} playing The playing event
   * @property {string} paused The stream has been paused
   * @property {string} stopped The stream has been stopped
   * @property {string} loading The stream is loading
   * @property {string} connecting Connecting to the stream
   * @property {string} waiting Waiting for stream
   * @property {string} streamFailed The stream failed to load
   * @property {string} stationFailed The station (all streams) failed to load
   * @property {string} nowPlayingChanged The stream has now changed to now playing
   * @property {string} positionChanged The position of the stream has changed
   * @property {string} mediaEnded The stream has finished
   * @property {string} qualifiedTune
   * @property {string} htmlStreamLoaded The html stream that is loaded
   * @property {string} nextTune The next stream
   * @property {string} evaluateStreamStart
   * @property {string} instreamAd The instream ad is playing
   * @property {string} meta HLS frag metadata emitted from StreamPlayer
   * @property {string} error Error emitted (for logging)
   * @property {string} playerSelected Emits the name of the selected player
   * @property {string} attemptingPlayback Attempting to load and play the stream
   * @property {string} initiateIntro The intro stream has begun playback
   * @property {string} initiateOutro The outro stream has begun playback
   * @property {string} endIntro The intro stream has ended playback
   * @property {string} endOutro The outro stream has ended playback
   */

  /**
   * @typedef params
   * @property {string} usPrivacyString
   * @property {boolean} gdprApplies
   * @property {boolean} isOptedOut
   * @property {string} ppid
   * @property {string} palNonce
   * @property {dfpParams} dfpParams
   * @property {string} audience
   */

  /**
   * @typedef dfpParams
   * @property {string} url
   * @property {string} description_url
   */

  /**
   * The tune object to load
   * @typedef {object} Tune
   * @property {string} guideId
   * @property {string} itemToken
   * @property {params} params
   * @property {string} title
   * @property {string} type
   */

  /**
   * @typedef {object} PlayerEvents
   */
  events: {
    playing: 'playing',
    paused: 'paused',
    stopped: 'stopped',
    loading: 'loading',
    connecting: 'connecting',
    waiting: 'waiting',
    streamFailed: 'streamFailed',
    stationFailed: 'stationFailed',
    nowPlayingChanged: 'nowPlayingChanged',
    positionChanged: 'positionChanged',
    mediaEnded: 'mediaEnded',
    qualifiedTune: 'qualifiedTune',
    htmlStreamLoaded: 'htmlStreamLoaded',
    nextTune: 'nextTune',
    evaluateStreamStart: 'evaluateStreamStart',
    instreamAd: 'instreamAd',
    meta: 'meta',
    error: 'error',
    playerSelected: 'playerSelected',
    attemptingPlayback: 'attemptingPlayback',
    initiateIntro: 'initiateIntro',
    initiateOutro: 'initiateOutro',
    endIntro: 'endIntro',
    endOutro: 'endOutro'
  },
  /**
   * Set up the player to play the stream
   * @function setUp
   * @param bundleId
   * @param playerElement
   * @param blankMp3Url
   * @param tuneInApiClient
   * @param shoutcastExtraTimeout
   * @param timeout
   * @param isInstreamLoggingEnabled
   * @param boostConfig
   * @param boostConfig.introMp3Url
   * @param boostConfig.outroMp3Url
   * @param boostConfig.introCap
   * @param boostConfig.outroCap
   * @param boostConfig.introTitle
   * @param boostConfig.outroTitle
   * @param isWebMediaPlayerEnabled
   * @param abTestIds
   * @param hasNativeHlsSupport
   * @param resources
   * @param forceHttpsStreams {boolean} upgrade all stream urls to https
   */
  setup: function setup(_ref) {
    var bundleId = _ref.bundleId,
      playerElement = _ref.playerElement,
      blankMp3Url = _ref.blankMp3Url,
      tuneInApiClient = _ref.tuneInApiClient,
      shoutcastExtraTimeout = _ref.shoutcastExtraTimeout,
      _ref$timeout = _ref.timeout,
      timeout = _ref$timeout === void 0 ? defaultTimeout : _ref$timeout,
      isInstreamLoggingEnabled = _ref.isInstreamLoggingEnabled,
      boostConfig = _ref.boostConfig,
      isWebMediaPlayerEnabled = _ref.isWebMediaPlayerEnabled,
      abTestIds = _ref.abTestIds,
      hasNativeHlsSupport = _ref.hasNativeHlsSupport,
      resources = _ref.resources,
      forceHttpsStreams = _ref.forceHttpsStreams,
      blockRejectedStreamsPlaybackMap = _ref.blockRejectedStreamsPlaybackMap,
      listenTimeConfig = _ref.listenTimeConfig;
    this.bundleId = bundleId;
    this.boostConfig = boostConfig;
    this.boostState = {
      introCount: 0,
      outroCount: 0
    };
    this.forceHttpsStreams = forceHttpsStreams;
    this.blockRejectedStreamsPlaybackMap = blockRejectedStreamsPlaybackMap;
    this.listenTimeConfig = listenTimeConfig;
    _debugger["default"].enabled = (0, _isDebugEnabled["default"])();
    _webApiClientSingleton["default"].setInstance(tuneInApiClient);
    (0, _streamUnwrap.setup)(timeout);
    (0, _ignoreChromeMediaEvents["default"])();
    this.streamPlayer = new _StreamPlayer["default"]({
      playerElement: playerElement,
      blankMp3Url: blankMp3Url,
      timeout: timeout,
      isInstreamLoggingEnabled: isInstreamLoggingEnabled,
      isWebMediaPlayerEnabled: isWebMediaPlayerEnabled,
      abTestIds: abTestIds,
      tuner: this,
      hasNativeHlsSupport: hasNativeHlsSupport,
      resources: resources
    });
    this.shoutcastExtraTimeout = shoutcastExtraTimeout;
    this.isAppleWebKitBrowser = (0, _isAppleWebkitBrowser["default"])();
  },
  /**
   * Load the tuner
   * @function load
   * @param {Tune} tune - The [Tune]{@link Tuner#Tune object} to set the player up with
   * @param {PlayerEvents} triggerEvent - The event that loads the player
   * @returns {Promise}
   */
  load: function () {
    var _load = (0, _asyncToGenerator2["default"])( /*#__PURE__*/_regenerator["default"].mark(function _callee(tune, triggerEvent) {
      var loadingPromise, wasPlayingBoost, loadMetadata;
      return _regenerator["default"].wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            if (this.tune && ![this.tune.guideId, this.boostState.guideId, this.boostState.parentGuideId].includes(tune.guideId)) {
              this.boostState = {
                introCount: 0,
                outroCount: 0
              };
            }
            loadingPromise = (0, _deferredPromise["default"])(); // Tune has not yet been set to this.tune - temporarily caching if previously tuned station was boost station
            wasPlayingBoost = this.tune && this.tune.guideId === this.boostState.guideId; // using previous tune and previous listen id
            _listenTime["default"].logStop(_objectSpread(_objectSpread({}, this.tune), {}, {
              listenId: this.previousListenId
            }));
            this.streamPlayer.clearMedia();
            this.tune = tune;
            this.streams = undefined;
            if (!this.shouldPlayBoostOutro(wasPlayingBoost)) {
              _context.next = 10;
              break;
            }
            _context.next = 10;
            return this.playBoostOutro();
          case 10:
            this.state.load(loadingPromise, triggerEvent);
            _context.next = 13;
            return loadingPromise.promise;
          case 13:
            loadMetadata = _context.sent;
            // If incoming tune guideId is the same as the boostGuideId, always play boost station
            if (this.tune.guideId === this.boostState.guideId) {
              this.playBoostStation();
            }
            return _context.abrupt("return", loadMetadata);
          case 16:
          case "end":
            return _context.stop();
        }
      }, _callee, this);
    }));
    function load(_x, _x2) {
      return _load.apply(this, arguments);
    }
    return load;
  }(),
  shouldPlayBoostOutro: function shouldPlayBoostOutro(wasPlayingBoost) {
    return !!(this.boostState.guideId &&
    // if the current station is boost eligible (boostState contains boostPrimary guideId from nowPlaying response)
    wasPlayingBoost &&
    // and if the previously tuned station was a boost stream
    this.tune.guideId === this.boostState.parentGuideId && (
    // and if the incoming tune guideId matches the directory stream guideId
    this.boostConfig.outroCap < 0 || this.boostState.outroCount < this.boostConfig.outroCap));
  },
  /**
   * Play the stream
   * @function play
   * @event StreamPlayer#play
   */
  play: function play() {
    this.state.play();
  },
  /**
   * Pauses the stream
   * @function play
   * @event StreamPlayer#pause
   */
  pause: function pause() {
    this.state.pause();
  },
  /**
   * Stops the stream
   * @function stop
   * @event StreamPlayer#stop
   */
  stop: function stop() {
    this.state.stop();
  },
  /**
   * Scrubs the stream to a new position
   * @function seek
   * @param {number} position
   * @event StreamPlayer#seek
   */
  seek: function seek(position) {
    this.state.seek(position);
  },
  /**
   * Sets the volume on the player
   * @function setVolume
   * @param {number} volume
   */
  setVolume: function setVolume(volume) {
    this.streamPlayer.setVolume(volume);
  },
  /**
   * Toggles the mute state of the player
   * @function toggleMute
   * @event StreamPlayer#toggleMute
   */
  toggleMute: function toggleMute() {
    this.state.muted = !this.state.muted;
    this.streamPlayer.toggleMute();
  },
  /**
   * Requests ads to be played in stream
   * @function requestInstreamAds
   * @event StreamPlayer#requestInstreamAds
   */
  requestInstreamAds: function requestInstreamAds() {
    this.streamPlayer.requestInstreamAds();
  },
  setInstreamCustParams: function setInstreamCustParams(custParamsPromise) {
    // passed from mint on profile change
    this.instreamCustParamsPromise = custParamsPromise;
  },
  /**
   * Returns the current volume
   * @function getVolume
   * @returns {Number}
   */
  getVolume: function getVolume() {
    return this.streamPlayer.getVolume();
  },
  // rate needs to be a double i.e. 1.0, 2.0
  changePlaybackRate: function changePlaybackRate(rate) {
    this.streamPlayer.changePlaybackRate(rate);
  },
  canChangePlaybackRate: function canChangePlaybackRate() {
    return this.streamPlayer.canChangePlaybackRate();
  },
  /**
   * Change the state of the state machine
   * @param state
   * @param context
   */
  changeState: function changeState(state, context) {
    if (this.state !== state) {
      _debugger["default"].log("".concat(this.state.name, " -> ").concat(state.name));
      if ((0, _isFunction["default"])(this.state.exit)) {
        // defer used in case there are pending promises that should be cancelled
        (0, _defer["default"])(this.state.exit);
      }
      var previousState = this.state;
      this.state = state;
      // defer used to ensure next state isn't entered before previous state is exited
      (0, _defer["default"])(this.state.enter, previousState, context);
    }
  },
  /**
   * Get the current state of the state machine
   * @function getStateName
   * @param state
   * @returns {string} Current state of machine
   */
  getStateName: function getStateName(state) {
    var states = this.states;
    var stateKeys = (0, _keys["default"])(states);
    return (0, _find["default"])(stateKeys, function (key) {
      return states[key] === state;
    });
  },
  getPlayerName: function getPlayerName() {
    var _ref2 = this.streamPlayer || {},
      player = _ref2.player;
    return player && player.name || '';
  },
  /**
   * Called from gemini-web to dispatch actions in response to tuner events
   * @function on
   * @listens module:Tuner~PlayerEvents
   * @param event The player event being emitted
   * @param {function} eventHandler The callback event handler
   */
  on: emitter.on.bind(emitter),
  /**
   * Remove the event handler
   * @function off
   * @param {EventSource} event
   * @param {function} eventHandler The callback event handler
   */
  off: function off(event, eventHandler) {
    // called from gemini-web to remove event handlers from tuner events
    if (event) {
      emitter.off(event, eventHandler);
    } else {
      (0, _each["default"])(this.events, function (_event) {
        emitter.removeAllListeners(_event);
      });
    }
  },
  /**
   * Fires the event
   * @function trigger
   * @param {PlayerEvents} event One of the Player events
   * @param {object} data The data to emit
   */
  trigger: emitter.emit.bind(emitter),
  /**
   * Generate a unique listenID
   * @function generate
   * @returns {number}
   */
  setListenId: function setListenId(listenId) {
    this.previousListenId = this.listenId;
    this.listenId = listenId;
  },
  stopPoller: function stopPoller() {
    _nowPlayingPoller["default"].stop();
  },
  preTune: function preTune() {
    this.streamPlayer.preload();
  },
  /**
   * Play Boost Station
   * @function playBoostStation
   */
  playBoostStation: function playBoostStation() {
    this.state.playBoostExperience();
  },
  /**
   * Play the Boost Outro
   * Private method aka should only be called internally by web-tuner
   * @function playBoostOutro
   * @returns {Promise}
   */
  playBoostOutro: function () {
    var _playBoostOutro = (0, _asyncToGenerator2["default"])( /*#__PURE__*/_regenerator["default"].mark(function _callee2() {
      var _this = this;
      return _regenerator["default"].wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            _context2.next = 2;
            return new Promise(function (resolve) {
              _this.changeState(_this.states.connecting, {
                shouldLogStatus: true,
                onBoostOutroEnded: function onBoostOutroEnded() {
                  _nowPlayingPoller["default"].toggleBoostMetadata(false);
                  resolve();
                }
              });
            });
          case 2:
            delete this.boostState.parentGuideId;
            this.boostState.outroCount++;
          case 4:
          case "end":
            return _context2.stop();
        }
      }, _callee2, this);
    }));
    function playBoostOutro() {
      return _playBoostOutro.apply(this, arguments);
    }
    return playBoostOutro;
  }()
};
var _default = exports["default"] = tuner;
//# sourceMappingURL=tuner.js.map